import { useEffect, useMemo, useState } from 'react';
import { axiosClient } from '../../auth-lib';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { Button, StatusPill, Table, EmptyState, useToast } from '../../design-system';
import '../styles/AdminOrganizations.css';

function AdminOrganizations() {
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const toast = useToast();
  // `?q=` comes from the top-bar search (org-admin parity). Filter client-side
  // over the already-loaded list — the org count per platform is small.
  const [searchParams] = useSearchParams();
  const query = (searchParams.get('q') || '').trim().toLowerCase();

  useEffect(() => {
    fetchOrganizations();
  }, []);

  const fetchOrganizations = async () => {
    try {
      const response = await axiosClient.get('/organizations');
      setOrganizations(response.data);
    } catch (error) {
      toast.danger('Failed to load organizations');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (orgId, orgName) => {
    if (!window.confirm(`Delete "${orgName}"? This cannot be undone.`)) return;
    try {
      await axiosClient.delete(`/organizations/${orgId}`);
      setOrganizations((prev) => prev.filter((o) => o._id !== orgId));
      toast.success('Organization deleted');
    } catch (error) {
      toast.danger(error.response?.data?.error || 'Failed to delete organization');
    }
  };

  const visibleOrgs = useMemo(() => {
    if (!query) return organizations;
    return organizations.filter((o) =>
      [o.name, o.industry, o.contactEmail].some((v) => (v || '').toLowerCase().includes(query))
    );
  }, [organizations, query]);

  const columns = [
    { key: 'name', header: 'Name', render: (o) => <strong>{o.name}</strong> },
    { key: 'industry', header: 'Industry', render: (o) => o.industry || '-' },
    { key: 'plan', header: 'Plan', render: (o) => o.subscription.plan },
    { key: 'status', header: 'Status', render: (o) => <StatusPill status={o.subscription.status} /> },
    { key: 'users', header: 'Users', render: (o) => o.userCount },
    { key: 'email', header: 'Contact Email', render: (o) => o.contactEmail || '-' },
    {
      key: 'actions',
      header: '',
      render: (o) => (
        <div className="row-actions">
          <button className="action-link" onClick={() => navigate(`/admin/organizations/${o._id}`)}>View</button>
          <button className="action-link" onClick={() => navigate(`/admin/organizations/${o._id}/edit`)}>Edit</button>
          <button className="action-link delete" onClick={() => handleDelete(o._id, o.name)}>Delete</button>
        </div>
      ),
    },
  ];

  return (
    <AdminLayout title="Organizations Management" subtitle="Manage every organization on the platform">
      <div className="admin-orgs-page">
        <div className="page-toolbar">
          {query && (
            <span className="admin-orgs-search-note">
              Showing {visibleOrgs.length} of {organizations.length} for “{query}”
            </span>
          )}
          <Button variant="primary" onClick={() => navigate('/admin/organizations/create')}>
            + Create Organization
          </Button>
        </div>

        {!loading && organizations.length === 0 ? (
          <EmptyState
            icon="🏢"
            title="No organizations yet"
            action={<Button variant="secondary" onClick={() => navigate('/admin/organizations/create')}>Create First Organization</Button>}
          />
        ) : !loading && visibleOrgs.length === 0 ? (
          <EmptyState icon="🔎" title="No matches" description={`No organizations match “${query}”.`} />
        ) : (
          <Table columns={columns} data={visibleOrgs} loading={loading} />
        )}
      </div>
    </AdminLayout>
  );
}

export default AdminOrganizations;
